import * as z from "zod";
import { decimal, pagination, uuidV7, version } from "../index";
import { retryWaitingDto, retryWaitingInput, syncStatuses, waitingItemCodes, waitingSources } from "../til";
import { shipmentStatuses, shipmentWaitingReasons } from "./enums";
import { fulfillmentParams } from "./fulfillment";

// Shipments of a sales order (M13, FR-M13-02, I-11, ADR-130; 50-api/logistics.md): what left the warehouse, as the
// warehouse reported it, and the tracking the Customer Service books by hand for the DDP in F1. A shipment of the
// warehouse adds its units to the lines of the fulfillment; one recorded by hand only tells where the goods are.

/** The shipments of a fulfillment, the first one first. */
export const listShipmentsInput = fulfillmentParams;
export type ListShipmentsInput = z.input<typeof listShipmentsInput>;

/** What the Customer Service says of a shipment it booked (FR-M13-02): the carrier and its tracking. */
const trackingFields = z.object({
  /** Gsped, DHL…: as the Customer Service names it. */
  carrier: z.string().trim().min(1).max(60),
  service: z.string().trim().min(1).max(60).nullable(),
  /** One for each parcel. */
  trackingNumbers: z.array(z.string().trim().min(1).max(100)).min(1).max(50),
  /** When the goods left; null while they are only booked. */
  shippedAt: z.iso.datetime().nullable(),
  notes: z.string().trim().max(2000).nullable(),
});

/**
 * `recordShipmentTracking`: a shipment booked by hand for a fulfillment (FR-M13-02), with the id from the client
 * (ADR-046). Not for an order the customer collects (EXW), nor for a fulfillment cancelled.
 */
export const recordShipmentTrackingInput = trackingFields
  .partial({ service: true, shippedAt: true, notes: true })
  .extend({ fulfillmentId: z.uuid(), id: uuidV7 });
export type RecordShipmentTrackingInput = z.input<typeof recordShipmentTrackingInput>;
export const recordShipmentTrackingBody = recordShipmentTrackingInput.omit({ fulfillmentId: true });

/** PATCH of a shipment recorded by hand: an absent field stays as it is, `null` empties it. */
export const updateShipmentTrackingInput = trackingFields.partial().extend({ shipmentId: z.uuid(), version });
export type UpdateShipmentTrackingInput = z.input<typeof updateShipmentTrackingInput>;
export const shipmentParams = updateShipmentTrackingInput.pick({ shipmentId: true });
export const updateShipmentTrackingBody = updateShipmentTrackingInput.omit({ shipmentId: true });

// --- Labels and pickups asked to the TLG shipping platform (F2, I-18, I-33, B-52, ADR-231) ---

/** A parcel of a booking: its weight, and its sizes when the Customer Service knows them (I-18 `parcels`). */
export const bookingParcel = z.object({
  /** Kilograms, up to three decimals. */
  weightKg: z
    .string()
    .trim()
    .regex(/^\d{1,5}(\.\d{1,3})?$/)
    .refine((kg) => Number(kg) > 0, "A parcel weighs something"),
  lengthCm: z.number().int().min(1).max(1000).optional(),
  widthCm: z.number().int().min(1).max(1000).optional(),
  heightCm: z.number().int().min(1).max(1000).optional(),
});
export type BookingParcel = z.output<typeof bookingParcel>;

/**
 * `bookShipment`: a label and a pickup asked to the TLG shipping platform for a DDP shipment of an order (B-52), with
 * the id from the client (ADR-046). The platform picks the carrier, and answers with its tracking and label (I-33).
 */
export const bookShipmentInput = z.object({
  fulfillmentId: z.uuid(),
  id: uuidV7,
  /** When the warehouse hands the goods over. */
  pickupDate: z.iso.date(),
  /** A service of the platform, in its code, when the Customer Service asks for one. */
  service: z.string().trim().min(1).max(60).optional(),
  parcels: z.array(bookingParcel).min(1).max(500),
});
export type BookShipmentInput = z.input<typeof bookShipmentInput>;
export const bookShipmentBody = bookShipmentInput.omit({ fulfillmentId: true });

/** `cancelShipmentBooking`: the booking withdrawn from the platform (I-18 `cancelled`), or at once if it never had it. */
export const cancelShipmentBookingInput = z.object({ shipmentId: z.uuid(), version });
export type CancelShipmentBookingInput = z.input<typeof cancelShipmentBookingInput>;
export const cancelShipmentBookingBody = cancelShipmentBookingInput.omit({ shipmentId: true });

/** `downloadShipmentLabel`: the label the platform made, as a stream (ADR-069). */
export const downloadShipmentLabelInput = z.object({ shipmentId: z.uuid() });
export type DownloadShipmentLabelInput = z.input<typeof downloadShipmentLabelInput>;

export const shipmentDto = z
  .object({
    id: z.uuid(),
    fulfillmentId: z.uuid(),
    /**
     * `warehouse`: reported by the warehouse (I-11), read only; `manual`: recorded by the Customer Service; `platform`:
     * asked to the TLG shipping platform, which writes its carrier and tracking (I-18, I-33).
     */
    source: z.enum(["warehouse", "manual", "platform"]),
    /** The number of the shipment in the system of the warehouse. */
    shipmentRef: z.string().nullable(),
    carrier: z.string().nullable(),
    service: z.string().nullable(),
    trackingNumbers: z.array(z.string()),
    packages: z.number().int().nullable(),
    grossWeightKg: decimal.nullable(),
    shippedAt: z.iso.datetime().nullable(),
    status: z.enum(shipmentStatuses),
    notes: z.string().nullable(),
    /** The day of the pickup: asked of the platform, then the one it booked. */
    pickupDate: z.iso.date().nullable(),
    deliveredAt: z.iso.datetime().nullable(),
    /** The page of the carrier that follows the shipment, as the platform gave it; only `https`. */
    trackingUrl: z.url().nullable(),
    /** What went wrong, as the platform said it, while the status is `exception`. */
    exception: z.string().nullable(),
    /** Whether the platform's label is stored: `downloadShipmentLabel` gives it. */
    hasLabel: z.boolean(),
    /** A booking asked to the platform: its id there and how its request stands; null for the other sources. */
    booking: z
      .object({
        ref: z.string().nullable(),
        syncStatus: z.enum(syncStatuses),
        cancelRequested: z.boolean(),
        /** Whether `cancelShipmentBooking` takes it now. */
        cancellable: z.boolean(),
      })
      .nullable(),
    version: z.number().int(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: "Shipment" });

// --- Shipments of the warehouse Nexxt could not apply (I-11 §5) ---

const waitingFilters = { reason: z.enum(shipmentWaitingReasons).optional() };

/** The shipments of the warehouse that wait, the latest first: each lists at most 50 of its items (`itemCount`). */
export const listWaitingShipmentsInput = z.object({
  ...waitingFilters,
  ...pagination({ defaultSize: 50, maxSize: 100 }),
});
export type ListWaitingShipmentsInput = z.input<typeof listWaitingShipmentsInput>;

/** How many shipments wait with the same filters, and since when the oldest (ADR-086): the queue of the home. */
export const countWaitingShipmentsInput = z.object(waitingFilters);
export type CountWaitingShipmentsInput = z.input<typeof countWaitingShipmentsInput>;

/**
 * Applies again the shipments that wait, once the mapping, the SKU or the shipment is set right: each one applied
 * goes, the others wait with the reason of now. With `acceptExcess` Logistics takes the units shipped beyond the
 * release as shipped (ADR-199): they go on the last line of their SKU, as before ADR-178 §1.10.
 */
export const retryWaitingShipmentsInput = retryWaitingInput.extend({ acceptExcess: z.boolean().default(false) });
export type RetryWaitingShipmentsInput = z.input<typeof retryWaitingShipmentsInput>;
export const retryWaitingShipmentsDto = retryWaitingDto("WaitingShipmentsRetried");

/**
 * An item the reason of a shipment that waits is about, with its codes as the warehouse sent them; for
 * `over_released`, the units beyond what the shipment still had to ship (ADR-199).
 */
export const waitingShipmentItem = z.object({
  soLineNo: z.number().int().nullable(),
  ...waitingItemCodes,
  qtyOver: z.number().int().optional(),
});
export type WaitingShipmentItem = z.output<typeof waitingShipmentItem>;

/** A shipment of the warehouse that waits, whole: nothing of it is applied until all of it can be. */
export const waitingShipmentDto = z
  .object({
    id: z.uuid(),
    shipmentRef: z.string(),
    /** The number of the instruction, as the warehouse sent it. */
    fulfillmentNumber: z.string(),
    /** The system the fact came from (ADR-144): the warehouse and the items are in its codes. */
    source: z.enum(waitingSources),
    /** The warehouse in the codes of `source`. */
    locationCode: z.string(),
    shippedAt: z.iso.datetime(),
    reason: z.enum(shipmentWaitingReasons),
    /** The first of the items the reason is about: none for the shipment as a whole. */
    items: z.array(waitingShipmentItem),
    /** How many items the reason is about. */
    itemCount: z.number().int(),
    /** Since when it waits. */
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "WaitingShipment" });
